/**
 * BottomTabBar — admin-side mobile bottom nav.
 *
 * Renders only on mobile (< md). 4 primary destinations + a "More"
 * entry that opens the full sidebar drawer. Mirrors the contractor
 * portal pattern. Hidden when sidebar is open so it never collides
 * with the slide-over.
 */
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  LayoutDashboard, Home, BarChart3, MoreHorizontal,
} from 'lucide-react';

export function BottomTabBar({ alertCount = 0, onMore, hidden = false, hasPermission }) {
  const isMobile = useIsMobile();
  if (!isMobile || hidden) return null;

  const tabs = [
    { name: 'Home', path: '/dashboard', icon: LayoutDashboard, perm: 'dashboard:view' },
    { name: 'Properties', path: '/properties', icon: Home, perm: 'properties:view_all' },
    { name: 'Reports', path: '/reports', icon: BarChart3, perm: 'properties:view_all' },
  ].filter(t => !hasPermission || hasPermission(t.perm));

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 pb-[env(safe-area-inset-bottom)]"
      data-testid="admin-bottom-tabbar"
    >
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
        {tabs.map(t => (
          <li key={t.path}>
            <NavLink
              to={t.path}
              className={({ isActive }) => `flex flex-col items-center justify-center py-2 min-h-[56px] text-[10px] font-medium ${
                isActive
                  ? 'text-slate-900 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
              data-testid={`btab-${t.name.toLowerCase()}`}
            >
              {({ isActive }) => (
                <>
                  <t.icon className={`w-5 h-5 mb-0.5 ${isActive ? 'text-blue-600 dark:text-blue-400' : ''}`} />
                  <span>{t.name}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={onMore}
            className="w-full flex flex-col items-center justify-center py-2 min-h-[56px] text-[10px] font-medium text-slate-500 dark:text-slate-400 relative"
            data-testid="btab-more"
          >
            <MoreHorizontal className="w-5 h-5 mb-0.5" />
            <span>More</span>
            {alertCount > 0 && (
              <span className="absolute top-1 right-[28%] min-w-[16px] h-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
                {alertCount > 99 ? '99+' : alertCount}
              </span>
            )}
          </button>
        </li>
      </ul>
    </nav>
  );
}

export default BottomTabBar;
